import { createContext, useContext, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";

// Create context for lazy data loading
const LazyDataContext = createContext();

// Custom hook for lazy data context
const useLazyDataContext = () => {
  const context = useContext(LazyDataContext);
  if (!context) {
    throw new Error("useLazyDataContext must be used within a LazyDataProvider");
  }
  return context;
};

export { useLazyDataContext };

export function LazyDataProvider({ children }) {
  // State for different data types
  const [goals, setGoals] = useState({});
  const [milestones, setMilestones] = useState({});
  const [notes, setNotes] = useState({});
  const [wishlists, setWishlists] = useState({});
  const [themes, setThemes] = useState({});
  
  // Loading states
  const [loading, setLoading] = useState({
    goals: {},
    milestones: {},
    notes: {},
    wishlists: {},
    themes: {}
  });
  
  // Cache for loaded data
  const cache = useRef(new Map());
  
  // Cache expiration time (5 minutes)
  const CACHE_EXPIRY = 5 * 60 * 1000;

  // Check if data is cached and not expired
  const isCached = useCallback((key) => {
    const cached = cache.current.get(key);
    if (!cached) return false;
    
    const now = Date.now();
    return (now - cached.timestamp) < CACHE_EXPIRY;
  }, []);

  // Get cached data
  const getCachedData = useCallback((key) => {
    const cached = cache.current.get(key);
    return cached ? cached.data : null;
  }, []);

  // Set cached data
  const setCachedData = useCallback((key, data) => {
    cache.current.set(key, {
      data,
      timestamp: Date.now()
    });
  }, []);

  // Clear cache for specific data type
  const clearCache = useCallback((dataType, year = null) => {
    if (year) {
      const key = `${dataType}_${year}`;
      cache.current.delete(key);
    } else {
      // Clear all cache entries for this data type
      for (const [key] of cache.current) {
        if (key.startsWith(`${dataType}_`)) {
          cache.current.delete(key);
        }
      }
    }
  }, []);

  // Clear all cache
  const clearAllCache = useCallback(() => {
    cache.current.clear();
  }, []);

  // Generic data fetcher
  const fetchData = useCallback(async (endpoint, dataType, year = null) => {
    const key = year ? `${dataType}_${year}` : dataType;
    
    // Check cache first
    if (isCached(key)) {
      return getCachedData(key);
    }

    // Set loading state
    setLoading(prev => ({
      ...prev,
      [dataType]: {
        ...prev[dataType],
        [year || 'all']: true
      }
    }));

    try {
      const response = await fetch(endpoint, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ${dataType}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || `Failed to fetch ${dataType}`);
      }

      // Cache the data
      setCachedData(key, result.data);
      
      return result.data;
    } catch (error) {
      console.error(`Error fetching ${dataType}:`, error);
      throw error;
    } finally {
      // Clear loading state
      setLoading(prev => ({
        ...prev,
        [dataType]: {
          ...prev[dataType],
          [year || 'all']: false
        }
      }));
    }
  }, [isCached, getCachedData, setCachedData]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    const key = 'dashboard';
    
    if (isCached(key)) {
      return getCachedData(key);
    }

    setLoading(prev => ({ ...prev, dashboard: true }));

    try {
      const response = await fetch('/dashboard/dashboard-data', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard data: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch dashboard data');
      }

      setCachedData(key, result.data);
      return result.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, dashboard: false }));
    }
  }, [isCached, getCachedData, setCachedData]);

  // Fetch goals for specific year
  const fetchGoals = useCallback(async (year, page = 1, limit = 10) => {
    const endpoint = `/dashboard/goals/${year}?page=${page}&limit=${limit}`;
    const data = await fetchData(endpoint, 'goals', year);
    
    setGoals(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        [page]: data
      }
    }));
    
    return data;
  }, [fetchData]);

  // Fetch milestones for specific goal
  const fetchMilestones = useCallback(async (goalId) => {
    const endpoint = `/dashboard/goals/${goalId}/milestones`;
    const data = await fetchData(endpoint, 'milestones', goalId);
    
    setMilestones(prev => ({
      ...prev,
      [goalId]: data
    }));
    
    return data;
  }, [fetchData]);

  // Fetch notes for specific year
  const fetchNotes = useCallback(async (year, page = 1, limit = 10) => {
    const endpoint = `/dashboard/notes/${year}?page=${page}&limit=${limit}`;
    const data = await fetchData(endpoint, 'notes', year);
    
    setNotes(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        [page]: data
      }
    }));
    
    return data;
  }, [fetchData]);

  // Fetch wishlists for specific year
  const fetchWishlists = useCallback(async (year, page = 1, limit = 10) => {
    const endpoint = `/dashboard/wishlists/${year}?page=${page}&limit=${limit}`;
    const data = await fetchData(endpoint, 'wishlists', year);
    
    setWishlists(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        [page]: data
      }
    }));
    
    return data;
  }, [fetchData]);

  // Get loading state for specific data
  const isLoading = useCallback((dataType, year = null) => {
    return loading[dataType]?.[year || 'all'] || false;
  }, [loading]);

  // Get data for specific type and year
  const getData = useCallback((dataType, year = null, page = 1) => {
    switch (dataType) {
      case 'goals':
        return goals[year]?.[page] || null;
      case 'milestones':
        return milestones[year] || null;
      case 'notes':
        return notes[year]?.[page] || null;
      case 'wishlists':
        return wishlists[year]?.[page] || null;
      case 'themes':
        return themes[year] || null;
      default:
        return null;
    }
  }, [goals, milestones, notes, wishlists, themes]);

  // Update specific data (for optimistic updates)
  const updateData = useCallback((dataType, year, data, page = 1) => {
    switch (dataType) {
      case 'goals':
        setGoals(prev => ({
          ...prev,
          [year]: {
            ...prev[year],
            [page]: data
          }
        }));
        break;
      case 'milestones':
        setMilestones(prev => ({
          ...prev,
          [year]: data
        }));
        break;
      case 'notes':
        setNotes(prev => ({
          ...prev,
          [year]: {
            ...prev[year],
            [page]: data
          }
        }));
        break;
      case 'wishlists':
        setWishlists(prev => ({
          ...prev,
          [year]: {
            ...prev[year],
            [page]: data
          }
        }));
        break;
      case 'themes':
        setThemes(prev => ({
          ...prev,
          [year]: data
        }));
        break;
    }
  }, []);

  const contextValue = {
    // Data
    goals,
    milestones,
    notes,
    wishlists,
    themes,
    
    // Loading states
    loading,
    isLoading,
    
    // Data fetching functions
    fetchDashboardData,
    fetchGoals,
    fetchMilestones,
    fetchNotes,
    fetchWishlists,
    
    // Data access
    getData,
    updateData,
    
    // Cache management
    clearCache,
    clearAllCache,
    isCached,
    getCachedData
  };

  return (
    <LazyDataContext.Provider value={contextValue}>
      {children}
    </LazyDataContext.Provider>
  );
}

LazyDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
